import * as React from "react";
import { useState } from "react";
import { useQuery, keepPreviousData } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { EventsDefinitions } from "@graylog/server-api";
import i18n from "i18n";
import { FormGroup } from "components/bootstrap";
import { InputDescription, Select, useChipInput } from "components/common";
import useDebouncedValue from "hooks/useDebouncedValue";
const normalizeTag = (raw) => raw.trim().toLowerCase();
const MAX_TAGS = 64;
const MAX_TAG_LENGTH = 128;
const VALID_TAG_PATTERN = /^[a-z0-9_.-]+$/;
const SUGGESTION_LIMIT = 50;
const DEBOUNCE_MS = 300;
const isTooLong = (tag) => tag.length > MAX_TAG_LENGTH;
const hasInvalidChars = (tag) => !VALID_TAG_PATTERN.test(tag);
const quote = (s) => `"${s}"`;
const buildTooLongMessage = (tagsList) => {
  if (tagsList.length === 0) return null;
  return i18n.t("tags_editor.too_long", {
    ns: "event_definitions",
    count: tagsList.length,
    tags: tagsList.map(quote).join(", "),
    max: MAX_TAG_LENGTH
  });
};
const buildInvalidCharsMessage = (tagsList) => {
  if (tagsList.length === 0) return null;
  return i18n.t("tags_editor.invalid_characters", {
    ns: "event_definitions",
    count: tagsList.length,
    tags: tagsList.map(quote).join(", ")
  });
};
const validateTag = (tag) => {
  if (isTooLong(tag)) return buildTooLongMessage([tag]);
  if (hasInvalidChars(tag)) return buildInvalidCharsMessage([tag]);
  return null;
};
const TagsEditor = ({ tags, onChange, disabled = false, error = null }) => {
  var _a;
  const { t } = useTranslation("event_definitions");
  const [query, setQuery] = useState("");
  const [debouncedQuery] = useDebouncedValue(query, DEBOUNCE_MS);
  const { data, isFetching } = useQuery({
    queryKey: ["event-definitions", "tag-suggestions", debouncedQuery],
    queryFn: () => EventsDefinitions.suggestTags(debouncedQuery, SUGGESTION_LIMIT),
    placeholderData: keepPreviousData,
    staleTime: 3e4,
    enabled: !disabled
  });
  const isAtMax = tags.length >= MAX_TAGS;
  const suggestions = ((_a = data == null ? void 0 : data.tags) != null ? _a : []).filter((s) => !tags.includes(s)).map((value) => ({ value, label: value, disabled: isAtMax }));
  const { messages, flagDuplicateAttempt, selectProps, reactSelectProps } = useChipInput({
    values: tags,
    onChange,
    normalize: normalizeTag,
    validate: validateTag,
    max: MAX_TAGS,
    onInputChanged: setQuery,
    // At most one bucket per tag, so a message names one failure mode.
    describeInvalid: (invalid) => [
      buildTooLongMessage(invalid.filter(isTooLong)),
      buildInvalidCharsMessage(invalid.filter((tag) => !isTooLong(tag) && hasInvalidChars(tag)))
    ].filter(Boolean).join(" ") || null,
    describeDuplicate: (tag) => t("tags_editor.duplicate", { tag }),
    // Already offered as a suggestion, so a Create row would duplicate it.
    suppressCreate: (_raw, candidate) => suggestions.some((option) => option.value === candidate)
  });
  const allMessages = [...messages, isAtMax ? t("tags_editor.max_reached", { max: MAX_TAGS }) : null].filter(Boolean);
  const localValidationError = allMessages.length === 0 ? null : /* @__PURE__ */ React.createElement(React.Fragment, null, allMessages.map((m) => /* @__PURE__ */ React.createElement("div", { key: m }, m)));
  const combinedError = error != null ? error : localValidationError;
  return /* @__PURE__ */ React.createElement(FormGroup, { controlId: "event-definition-tags", validationState: combinedError ? "error" : null }, /* @__PURE__ */ React.createElement(
    Select,
    {
      ...selectProps,
      ...reactSelectProps,
      ...{
        onKeyDown: (e) => {
          if (e.key === "Enter" || e.key === "Tab") {
            flagDuplicateAttempt();
          }
        }
      },
      inputId: "event-definition-tags",
      "aria-label": t("tags_editor.aria_label"),
      multi: true,
      allowCreate: true,
      options: suggestions,
      isLoading: isFetching,
      disabled,
      placeholder: t("tags_editor.placeholder")
    }
  ), /* @__PURE__ */ React.createElement(InputDescription, { error: combinedError, help: t("tags_editor.help") }));
};
export default TagsEditor;
